import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { DashboardSkeleton } from '../../components/skeletons';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

export default function WalletOverview() {
  useAuth();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modal, setModal] = useState(null); // { vendorId, vendorName }
  const [adjForm, setAdjForm] = useState({ amount: '', type: 'credit', description: '' });
  const [adjusting, setAdjusting] = useState(false);

  const getCleanEmail = (email) => {
    if (!email) return '';
    if (email.startsWith('deleted_')) {
      const parts = email.split('_');
      if (parts.length >= 3) {
        return parts.slice(2).join('_');
      }
    }
    return email;
  };

  const getCleanMobile = (mobile) => {
    if (!mobile) return '';
    if (mobile.startsWith('deleted_')) {
      const parts = mobile.split('_');
      if (parts.length >= 3) {
        return parts.slice(2).join('_');
      }
    }
    return mobile;
  };

  const badge = (accountStatus) => {
    if (accountStatus === 'deleted') {
      return <span style={{ background: '#f3f4f6', color: MUTED, padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>Deleted</span>;
    }
    return <span style={{ background: '#dcfce7', color: GREEN, padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>Active</span>;
  };

  const loadWallets = () => {
    adminAPI.allWallets()
      .then(r => {
        const list = r.data.data || r.data || [];
        setWallets(list.filter(w => w.vendorId !== null && w.vendorId !== undefined));
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadWallets(); }, []);

  const handleAdjust = async () => {
    if (!adjForm.amount || !adjForm.description) return;
    setAdjusting(true);
    try {
      await adminAPI.adjustWallet(modal.vendorId, {
        amount: parseFloat(adjForm.amount),
        type: adjForm.type,
        description: adjForm.description,
      });
      setModal(null);
      setAdjForm({ amount: '', type: 'credit', description: '' });
      loadWallets();
    } catch (e) {
      alert(e.response?.data?.message || 'Adjustment failed');
    }
    setAdjusting(false);
  };

  const balanceColor = (bal) => {
    if (bal >= 2000) return GREEN;
    if (bal >= 500) return GOLD;
    return '#dc2626';
  };

  const filtered = wallets.filter(w => {
    if (!w.vendorId) return false;
    
    // Status Filter
    const isDeleted = w.vendorId?.accountStatus === 'deleted';
    if (statusFilter === 'active' && isDeleted) return false;
    if (statusFilter === 'deleted' && !isDeleted) return false;

    // Search Filter
    const q = search.toLowerCase();
    const name = w.vendorId?.fullName || '';
    const cleanEmail = getCleanEmail(w.vendorId?.email || '');
    const cleanMobile = getCleanMobile(w.vendorId?.mobileNumber || '');
    
    return !q || 
      name.toLowerCase().includes(q) || 
      cleanEmail.toLowerCase().includes(q) || 
      cleanMobile.toLowerCase().includes(q);
  });

  if (loading) return <DashboardSkeleton />;

  return (
    <div style={{ padding: 24, background: CREAM, minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: CHAR, margin: 0 }}>Wallet Overview</h1>
          <p style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>Monitor and adjust vendor wallet balances</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <select
            className="form-input"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ width: 140, fontSize: 13, background: '#fff', cursor: 'pointer' }}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="deleted">Deleted</option>
          </select>
          <input
            className="form-input"
            placeholder="Search by vendor..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 260, fontSize: 13 }}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Balance (All)', value: `₹${wallets.reduce((s, w) => s + (w.balance || 0), 0).toLocaleString('en-IN')}`, color: GREEN },
          { label: 'Total Wallets', value: wallets.length, color: PURPLE },
          { label: 'Low Balance (< ₹500)', value: wallets.filter(w => (w.balance || 0) < 500).length, color: '#dc2626' },
        ].map((s, i) => (
          <div key={i} className="card" style={{ padding: '16px 20px', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Wallets List */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['Vendor', 'Status', 'Balance', 'MTD Deductions', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `2px solid ${BORDER}` }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: MUTED, fontSize: 13 }}>No wallets found</td></tr>
              ) : (
                filtered.map(w => {
                  const name = w.vendorName || w.vendorId?.fullName || w.vendor?.fullName || 'Unknown Vendor';
                  const bal = w.balance || 0;
                  const mtd = w.mtdDeductions || w.deductionsMTD || 0;
                  return (
                    <tr key={w._id || w.vendorId} style={{ borderBottom: `1px solid ${BORDER}` }}>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: CHAR }}>{name}</div>
                        <div style={{ fontSize: 11, color: MUTED }}>{getCleanEmail(w.vendorId?.email || w.vendor?.email || w.email || '')}</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {badge(w.vendorId?.accountStatus)}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{
                          fontSize: 15, fontWeight: 700, color: balanceColor(bal),
                          padding: '2px 10px', borderRadius: 6,
                          background: bal >= 2000 ? '#dcfce7' : bal >= 500 ? '#fef3c7' : '#fee2e2',
                        }}>
                          ₹{bal.toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: MUTED }}>
                        ₹{mtd.toLocaleString('en-IN')}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <button
                          onClick={() => setModal({ vendorId: w.vendorId?._id || w.vendorId || w.vendor?._id || w._id, vendorName: name })}
                          style={{
                            background: PURPLE + '15', color: PURPLE, border: 'none', borderRadius: 6,
                            padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          Adjust
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Adjust Modal */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setModal(null)}>
          <div className="card" style={{ width: 420, padding: 28 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 700, color: CHAR, marginBottom: 6 }}>
              Adjust Wallet
            </h3>
            <p style={{ fontSize: 13, color: MUTED, marginBottom: 20 }}>
              Vendor: <strong style={{ color: CHAR }}>{modal.vendorName}</strong>
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 4, display: 'block' }}>Amount (₹) *</label>
                <input
                  className="form-input"
                  type="number"
                  value={adjForm.amount}
                  onChange={e => setAdjForm({ ...adjForm, amount: e.target.value })}
                  placeholder="0"
                  style={{ width: '100%' }}
                  onWheel={e => e.target.blur()}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 4, display: 'block' }}>Type *</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {['credit', 'debit'].map(t => (
                    <button
                      key={t}
                      onClick={() => setAdjForm({ ...adjForm, type: t })}
                      style={{
                        flex: 1, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        cursor: 'pointer',
                        border: `2px solid ${adjForm.type === t ? (t === 'credit' ? GREEN : '#dc2626') : BORDER}`,
                        background: adjForm.type === t ? (t === 'credit' ? '#dcfce7' : '#fee2e2') : '#fff',
                        color: adjForm.type === t ? (t === 'credit' ? GREEN : '#dc2626') : MUTED,
                      }}
                    >
                      {t === 'credit' ? '+ Credit' : '- Debit'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: CHAR, marginBottom: 4, display: 'block' }}>Description *</label>
                <input
                  className="form-input"
                  value={adjForm.description}
                  onChange={e => setAdjForm({ ...adjForm, description: e.target.value })}
                  placeholder="Reason for adjustment..."
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button
                onClick={() => setModal(null)}
                style={{ background: '#f3f4f6', color: MUTED, border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleAdjust}
                disabled={adjusting || !adjForm.amount || !adjForm.description}
                style={{
                  background: PURPLE, color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  opacity: (adjusting || !adjForm.amount || !adjForm.description) ? 0.6 : 1,
                }}
              >
                {adjusting ? 'Adjusting...' : 'Apply Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
